/**
 * Fill between sheet (W2-11 batch-tools; DESIGN.md §4.14, §5.8; RPE Curve notes CURVE-01..04, E§4.11).
 * Two notes on one line -> note type, spacing as a musical value ("every 1/16 beat") or a count, X curve, include
 * the end notes, properties from the start note or the user's new-note defaults. Live ghosts in the lane.
 * The X of the new notes is shown as the equivalent note-field expression (`fillExpression`), with Copy.
 */

import { useMemo, useState } from 'react';
import type { DialogProps } from '../../../app/registry/types';
import { LINEAR } from '../../../core/ease/index';
import type { Ease } from '../../../core/ease/types';
import type { NoteId, NoteKind } from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';
import { formatBeat } from '../../../core/time/beatSyntax';
import { fillExpression } from '../../../core/tools/fill';
import { Tools } from '../../../core/tools/index';
import type { FillParams, ToolReport } from '../../../core/tools/types';
import { Checkbox } from '../../../ui/Checkbox';
import { EmptyState } from '../../../ui/EmptyState';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import type { Json } from '../../../core/util/types';
import { m } from '../batch.messages';
import s from '../Batch.module.css';
import { EXT, extObject, isBool, isNum, isStr, pick, ref, remember, snapDiv, toastNotes } from '../batchContext';
import { CurveField, ExpressionBox, NumberInput, ToolSheet, useText, type SheetStatus } from '../sheetKit';
import { useToolGesture } from '../useToolGesture';

const KINDS: readonly NoteKind[] = ['tap', 'drag', 'flick', 'hold'];
const KIND_LABEL: Readonly<Record<NoteKind, string>> = {
  tap: m.kindTap,
  drag: m.kindDrag,
  flick: m.kindFlick,
  hold: m.kindHold,
};
/** Musical spacings offered (1/div beat). */
export const SPACING_DIVS: readonly number[] = [1, 2, 3, 4, 6, 8, 12, 16, 24, 32, 48, 64];

export function FillSheet(p: DialogProps<'fill'>) {
  const { services, props } = p;
  const text = useText();
  const store = services.store;
  const saved = useMemo(() => extObject(services, EXT.fill), [services]);
  const pair = useMemo(() => {
    const ids = props.notes.filter((id) => store?.doc.notes.has(id) === true);
    return ids.length === 2 ? (ids as unknown as readonly [NoteId, NoteId]) : null;
  }, [props.notes, store]);
  const sameLine = useMemo(() => {
    if (pair === null || store === null) return false;
    return store.doc.notes.get(pair[0])?.line === store.doc.notes.get(pair[1])?.line;
  }, [pair, store]);

  const [kind, setKind] = useState<NoteKind>(() => {
    const v = pick(saved, 'kind', isStr, 'drag');
    return (KINDS as readonly string[]).includes(v) ? (v as NoteKind) : 'drag';
  });
  const [spacingMode, setSpacingMode] = useState<'every' | 'count'>(() =>
    pick(saved, 'spacingMode', isStr, 'every') === 'count' ? 'count' : 'every',
  );
  const [div, setDiv] = useState(() => {
    const v = pick(saved, 'div', isNum, snapDiv(services) * 4);
    return SPACING_DIVS.includes(v) ? v : 16;
  });
  const [count, setCount] = useState(() => pick(saved, 'count', isNum, 8));
  const [curve, setCurve] = useState<Ease>(LINEAR);
  const [includeEnds, setIncludeEnds] = useState(() => pick(saved, 'includeEnds', isBool, false));
  const [propsFrom, setPropsFrom] = useState<'start' | 'template'>(() =>
    pick(saved, 'propsFrom', isStr, 'start') === 'template' ? 'template' : 'start',
  );

  const params = useMemo<FillParams | null>(() => {
    if (pair === null || !sameLine) return null;
    return {
      from: pair[0],
      to: pair[1],
      kind,
      spacing: spacingMode === 'every' ? { every: Beats.make(0, 1, div) } : { count },
      xCurve: curve,
      includeEnds,
      propsFrom,
      ...(propsFrom === 'template' ? { template: services.settings.value.editing.noteDefaults } : {}),
    };
  }, [pair, sameLine, kind, spacingMode, div, count, curve, includeEnds, propsFrom, services]);

  const build = useMemo(
    () => (params === null ? null : (tx: Parameters<typeof Tools.fillBetween>[0]) => Tools.fillBetween(tx, params)),
    [params],
  );
  const gesture = useToolGesture<ToolReport>({ services, label: ref(m.txnFill), command: 'tools.fill', build });

  const cancel = (): void => {
    gesture.cancel();
    p.close(null);
  };
  if (pair === null || !sameLine) {
    return (
      <ToolSheet
        title={text.t(m.fillTitle)}
        status={{ kind: 'none' }}
        applyLabel={text.t(m.generate)}
        applyDisabledReason={ref(pair === null ? m.fillNeedTwo : m.fillSameLine)}
        onApply={() => undefined}
        onCancel={cancel}
      >
        <EmptyState icon="Spline" title={ref(pair === null ? m.fillNeedTwo : m.fillSameLine)} body={ref(m.fillHowTo)} />
      </ToolSheet>
    );
  }

  const outcome = gesture.outcome;
  let status: SheetStatus = { kind: 'none' };
  if (gesture.status === 'paused') status = { kind: 'warn', text: text.t(m.previewPaused) };
  else if (outcome !== null && !outcome.ok) status = { kind: 'error', text: text.tr(outcome.reason) };
  else if (outcome?.ok === true) status = { kind: 'ok', text: text.t(m.fillStatus, { count: outcome.value.changed }) };

  const noteA = store?.doc.notes.get(pair[0]);
  const noteB = store?.doc.notes.get(pair[1]);
  const expression = noteA === undefined || noteB === undefined ? '' : fillExpression(noteA, noteB, curve);

  const apply = (): void => {
    const out = gesture.apply();
    if (out === null || !out.ok) return;
    const value: Json = { kind, spacingMode, div, count, includeEnds, propsFrom };
    remember(services, EXT.fill, value);
    services.ui.toast(ref(m.fillDone, { count: out.value.changed }), { kind: 'success', undo: out.txn !== null });
    toastNotes(services, out.notes);
    p.close({ applied: true });
  };

  return (
    <ToolSheet
      title={text.t(m.fillTitle)}
      subtitle={text.t(m.countNotes, { count: 2 })}
      status={status}
      applyLabel={text.t(m.generate)}
      onApply={apply}
      onCancel={cancel}
    >
      <Segmented<NoteKind>
        label={text.t(m.noteType)}
        value={kind}
        options={KINDS.map((k) => ({ value: k, label: text.t(KIND_LABEL[k]) }))}
        onChange={setKind}
      />
      <Segmented<'every' | 'count'>
        label={text.t(m.spacing)}
        value={spacingMode}
        options={[
          { value: 'every', label: text.t(m.spacingEvery) },
          { value: 'count', label: text.t(m.spacingCount) },
        ]}
        onChange={setSpacingMode}
      />
      {spacingMode === 'every' ? (
        <Select<string>
          label={text.t(m.spacingEveryBeat)}
          value={String(div)}
          options={SPACING_DIVS.map((d) => ({
            value: String(d),
            label: text.t(m.beatFraction, { beat: formatBeat(Beats.make(0, 1, d)) }),
          }))}
          onChange={(v) => {
            setDiv(Number(v));
          }}
        />
      ) : (
        <NumberInput label={text.t(m.spacingCountLabel)} value={count} integer min={1} max={4096} onChange={setCount} />
      )}
      <CurveField services={services} label={text.t(m.xCurve)} value={curve} onChange={setCurve} />
      {expression !== '' && (
        <ExpressionBox
          services={services}
          label={text.t(m.equivalentExpression)}
          code={expression}
          note={text.t(m.fillExpressionNote)}
          copyable
        />
      )}
      <Checkbox className={s.check} label={text.t(m.includeEnds)} checked={includeEnds} onChange={setIncludeEnds} />
      <Segmented<'start' | 'template'>
        label={text.t(m.propsFrom)}
        value={propsFrom}
        options={[
          { value: 'start', label: text.t(m.propsStart) },
          { value: 'template', label: text.t(m.propsDefaults) },
        ]}
        onChange={setPropsFrom}
      />
      <p className={s.hint}>{text.t(m.fillHint)}</p>
    </ToolSheet>
  );
}
